import { Avatar, Clickable, Col, Dropdown, Icon, Row, Spinner2, Text } from 'components';
import { useInfiniteScrollObserver } from 'core';
import { Api, theme } from 'lib';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Header,
  IrreversableModal,
  ParticipationSummary,
  SearchUsers
} from 'screens/Admin/components';
import { FancamPanel, List } from './components';

const fmtNumber = (n) => (n || 0).toLocaleString();

// Names the exact slice the counts describe: format first, then who they belong to. With
// both a team and a user picked the user is the narrower of the two, so it leads.
const summaryNote = (typeOpt, team, owner) => {
  const subject =
    typeOpt === 'live'
      ? 'Total Fancam Lives'
      : typeOpt === 'challenge'
        ? 'Total Fancam Challenges'
        : 'Total fancams';

  if (team && owner) return `${subject} for ${owner.name} in ${team.name}`;
  if (team) return `${subject} for ${team.name}`;
  if (owner) return `${subject} for ${owner.name}`;

  return `${subject} across every channel`;
};

// Start paging before the list actually bottoms out, so the request and the row append
// happen while there is still content to scroll through. Without this the observer only
// fired once the sentinel was fully on screen - by which point the user was already jammed
// against the end of the list, so the work read as a freeze rather than as loading.
//
// Sized against how far a fast flick travels while a request is in flight, not against the
// page: the server returns 50 rows at 53px each, so one page is ~2650px. Keep this well
// under that - at or above a page height the newly appended rows land inside the margin and
// the next page fetches immediately, chaining pages nobody has scrolled towards.
const PAGE_AHEAD_PX = 1500;

const typeOptions = [
  { value: 'All' },
  { value: 'Live', opt: 'live' },
  { value: 'Challenge', opt: 'challenge' }
];

const Fancams = () => {
  const [typeFilter, setTypeFilter] = useState({ value: 'All' }),
    [teamFilter, setTeamFilter] = useState(null),
    [ownerFilter, setOwnerFilter] = useState(null),
    [dataId, setDataId] = useState(null),
    [pendingDelete, setPendingDelete] = useState(null),
    [deleting, setDeleting] = useState(false),
    [loading, setLoading] = useState(true),
    // Distinct from loading: loading blanks the table for the first page and for filter
    // changes, while paging keeps the rows on screen and shows a spinner underneath them.
    [paging, setPaging] = useState(false),
    [summary, setSummary] = useState(null),
    // Starts true so the first paint is the loader, not a momentary row of zeros.
    [summaryLoading, setSummaryLoading] = useState(true),
    fancams = useSelector((state) => state.admin?.fancams),
    fetching = useRef(false),
    // Only ever set when the server returns an empty page. It used to double as "the
    // sentinel is in view", and conflating the two is what let a fetch fire against a stale
    // flag; sentinel position is now measured at the point of use instead.
    endReached = useRef(false),
    // Row count the auto-fill effect below last acted on, so it only reacts to the list
    // actually growing and can not spin on a page that dedupes down to nothing new.
    lastFilled = useRef(0),
    skip = useRef(0),
    dispatch = useDispatch(),
    observerTarget = useRef(null),
    // The element that actually scrolls — the Col below sets `overflow: scroll` via `scrollY`,
    // so the page itself never scrolls this list. State rather than a ref because the observer
    // options have to rebuild once the node exists, and a ref mutating to non-null does not
    // re-render.
    [scrollEl, setScrollEl] = useState(null);

  // Aggregate counts for the current team/user scope. Independent of the list's paging —
  // these describe the whole filtered set, not just the rows loaded so far.
  const getSummary = useCallback(async () => {
    setSummaryLoading(true);

    const params = new URLSearchParams();
    if (teamFilter) params.set('teamId', teamFilter._id);
    if (ownerFilter) params.set('ownerId', ownerFilter._id);
    const qs = params.toString();

    const res = await Api.get(`/admin/fancam/summary${qs ? `?${qs}` : ''}`, null, {
      silent: true
    });

    setSummary(res?.data || null);
    setSummaryLoading(false);
  }, [teamFilter, ownerFilter]);

  // Memoized on the filters alone, so it keeps a stable identity across the renders paging
  // and the summary fetch cause. useInfiniteScrollObserver keys its effect on this callback,
  // so an unstable identity meant tearing down and rebuilding the observer every render.
  const getFancams = useCallback(
    async (isFilterChange) => {
      if (isFilterChange) {
        dispatch({ type: 'ADMIN_SET_FANCAMS', payload: [] });
        setLoading(true);
        skip.current = 0;
        endReached.current = false;
      }

      if (fetching.current || endReached.current) return;

      fetching.current = true;
      if (!isFilterChange) setPaging(true);

      try {
        const res = await Api.post('/admin/fancam/list', {
          skip: skip.current,
          ...(typeFilter.opt ? { type: typeFilter.opt } : {}),
          ...(teamFilter ? { teamId: teamFilter._id } : {}),
          ...(ownerFilter ? { ownerId: ownerFilter._id } : {})
        });

        setLoading(false);

        if (!res || res.length === 0) {
          endReached.current = true;
          return;
        }

        skip.current += res.length;

        dispatch({ type: 'ADMIN_ADD_FANCAMS', payload: res });
      } finally {
        // Released only once the appended rows are in flight to the store, so the
        // intersection that arrives next cannot fetch the same page twice. In a finally so a
        // failed request cannot wedge paging shut.
        fetching.current = false;
        if (!isFilterChange) setPaging(false);
      }
    },
    [dispatch, typeFilter, teamFilter, ownerFilter]
  );

  // useInfiniteScrollObserver requires this callback, but the auto-fill effect below reads
  // the sentinel position directly instead of trusting a flag the observer sets
  // asynchronously, so there is nothing to record here.
  const bottomTrigger = useCallback(() => {}, []);

  // `root` is the scroll container, not the default viewport. This list scrolls inside an
  // overflow element, and rootMargin only ever expands the observer's own root — it does
  // nothing about the clip an intermediate scrolling ancestor imposes. Left on the viewport the
  // sentinel had to physically enter the container's visible box before it could intersect at
  // all, so the page-ahead margin above bought nothing; and because the container is 100vh tall
  // starting 54px down, its bottom edge — where the sentinel comes to rest — sits below the
  // viewport entirely, where a zero-height target can never reach `threshold: 1`. Rooted here
  // the margin expands the container's own box and paging fires PAGE_AHEAD_PX early as intended.
  const pageAhead = useMemo(
    () => ({ root: scrollEl, rootMargin: PAGE_AHEAD_PX + 'px 0px' }),
    [scrollEl]
  );

  useInfiniteScrollObserver(observerTarget, getFancams, bottomTrigger, pageAhead);

  useEffect(() => {
    getFancams(true).then();
    getSummary().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fetching.current || loading) return;
    setDataId(null);
    getFancams(true).then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, teamFilter, ownerFilter]);

  // The header counts are themselves the split by format, so only the team/user filters
  // narrow them — re-fetching on typeFilter would zero out the format filtered away.
  useEffect(() => {
    if (loading) return;
    getSummary().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamFilter, ownerFilter]);

  // A page short enough to leave the sentinel still on screen produces no new intersection
  // event, so keep pulling until it is pushed out of view or the list runs dry. The observer
  // used to be rebuilt on every render, and it was that churn re-firing the callback that
  // happened to cover this case.
  useEffect(() => {
    const count = fancams?.length || 0;

    if (count === 0) {
      lastFilled.current = 0;
      return;
    }

    if (count <= lastFilled.current) return;
    if (endReached.current || fetching.current) return;

    // Measured rather than flag-based: the observer reports asynchronously, so on
    // the commit that appended these rows its flag can still say the sentinel is in view
    // when the new rows have already pushed it well past the margin. Trusting it there
    // would fetch a page nobody has scrolled towards yet.
    const sentinel = observerTarget.current;
    if (!sentinel) return;
    // Against the scroll container's bottom edge rather than the window's: the container is the
    // thing that scrolls, and its bottom is not the viewport's.
    const visibleBottom = scrollEl ? scrollEl.getBoundingClientRect().bottom : window.innerHeight;
    if (sentinel.getBoundingClientRect().top > visibleBottom + PAGE_AHEAD_PX) return;

    lastFilled.current = count;
    getFancams().then();
  }, [fancams, getFancams, scrollEl]);

  const handleRowClick = useCallback((id) => setDataId(id), []);

  const handleRequestDelete = useCallback((item) => setPendingDelete(item), []);

  const closePanel = useCallback(() => setDataId(null), []);

  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  async function handleConfirmDelete() {
    if (!pendingDelete) return;

    const fancamId = pendingDelete._id;

    setDeleting(true);
    const res = await Api.delete(`/admin/fancam/delete/${fancamId}`);
    setDeleting(false);

    if (res) {
      dispatch({ type: 'ADMIN_REMOVE_FANCAM', payload: fancamId });
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Fancam deleted successfully' } });

      if (dataId === fancamId) setDataId(null);
      getSummary().then();
    }

    setPendingDelete(null);
  }

  const summaryItems = useMemo(
    () =>
      [
        { label: 'Total Fancams', value: fmtNumber(summary?.total) },
        {
          label: 'Live',
          filteredLabel: 'Total Lives',
          value: fmtNumber(summary?.live),
          type: 'live'
        },
        {
          label: 'Challenge',
          filteredLabel: 'Total Challenges',
          value: fmtNumber(summary?.challenge),
          type: 'challenge'
        }
      ]
        // Filtering the list to one format leaves only that format's count: the other
        // format describes rows the list isn't showing, and the total just restates
        // the one that's left.
        .filter((item) => (typeFilter.opt ? item.type === typeFilter.opt : true))
        // Left on its own, that count *is* the total, so it takes the fuller name.
        .map((item) => (typeFilter.opt ? { ...item, label: item.filteredLabel } : item)),
    [summary, typeFilter.opt]
  );

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center>
          <Text h3>Fancams</Text>
        </Row>
      </Header>

      <Col ref={setScrollEl} start noFlex marg="54px 0 0" wid="100%" ht="100vh" scrollY>
        <Row start gap="30px" pad="12px 24px" noFlex>
          <Dropdown
            admin
            title="Filter by Type"
            wid="200px"
            selected={typeFilter.value}
            options={typeOptions}
            handleSelect={setTypeFilter}
            rounded
            small
          />

          <Col noFlex>
            <Text marg="0 16px 4px" b2 med>
              Filter by Team
            </Text>
            <Row noFlex wid="300px">
              <SearchUsers
                userType="team"
                selected={teamFilter}
                handleSelect={setTeamFilter}
                includeDummy={true}
                noRounded
              />
              {teamFilter && (
                <Row start between wid="100%" center>
                  <Row noFlex center>
                    <Avatar small user={teamFilter} />
                    <Col marg="0 0 0 8px" noFlex ht="40px">
                      <Text>{teamFilter.name}</Text>
                    </Col>
                  </Row>
                  <Clickable onClick={() => setTeamFilter(null)}>
                    <Icon type="close" dimensions={14} />
                  </Clickable>
                </Row>
              )}
            </Row>
          </Col>

          <Col noFlex>
            <Text marg="0 16px 4px" b2 med>
              Filter by User
            </Text>
            <Row noFlex wid="300px">
              <SearchUsers
                userType="fan"
                selected={ownerFilter}
                handleSelect={setOwnerFilter}
                noRounded
              />
              {ownerFilter && (
                <Row start between wid="100%" center>
                  <Row noFlex center>
                    <Avatar small user={ownerFilter} />
                    <Col marg="0 0 0 8px" noFlex>
                      <Text>{ownerFilter.name}</Text>
                      <Text c1 col={theme.GREY_60}>
                        @{ownerFilter.username}
                      </Text>
                    </Col>
                  </Row>
                  <Clickable onClick={() => setOwnerFilter(null)}>
                    <Icon type="close" dimensions={14} />
                  </Clickable>
                </Row>
              )}
            </Row>
          </Col>
        </Row>

        <Col noFlex wid="100%" pad="0 24px">
          <ParticipationSummary
            loading={summaryLoading}
            // note={summaryNote(typeFilter.opt, teamFilter, ownerFilter)}
            items={summaryItems}
          />
        </Col>

        <List
          data={fancams}
          loading={loading}
          dataId={dataId}
          onClick={handleRowClick}
          onRequestDelete={handleRequestDelete}
        />
        {paging && (
          <Col wid="100%" ht="140px" marg="8px 0 24px" centerAll noFlex>
            <Spinner2 spinnerSize="44px" />
          </Col>
        )}

        <div ref={observerTarget} />
      </Col>

      <FancamPanel dataId={dataId} closeModal={closePanel} />

      {/* One confirmation for the whole screen. Mounted per row, this pulled a store
          subscription and a document keydown listener into every row of an unbounded list. */}
      <IrreversableModal
        visible={!!pendingDelete}
        onConfirm={handleConfirmDelete}
        closeModal={cancelDelete}
        noOutsideClick={true}
        waiting={deleting}
        confirmText="Delete"
        heading="You are about to delete this fancam."
      />
    </Col>
  );
};

export default Fancams;
